import { isSameDay, startOfDay } from "date-fns";
import groupBy from "lodash/groupBy";
import { useCallback, useMemo } from "react";
import { SectionList, StyleSheet } from "react-native";

import {
  type Chat,
  type ChatMessage as ChatMessageType,
  formatDay,
  getDateFromServerTimestamp,
} from "../services";
import { Row, Spinner, TextLabel } from "../lib";
import { colors, createStyles } from "../theme";
import { ChatMessage } from "./chat-message";
import { Banner } from "./banner";

type Props = {
  chat: Chat | undefined;
  disabled?: boolean;
  onRefresh: () => Promise<void>;
  typing?: boolean;
};

export function ChatMessages({
  chat,
  disabled = false,
  onRefresh,
  typing = false,
}: Props) {
  // TODO
  //   if (chat.messages.length !== oldChat.messages.length) {
  //     this.scrollToBottom();
  //   }

  // Vars
  const isLoading = chat === undefined;
  const messagesByDay = useMemo(() => {
    const result = groupBy(chat?.messages ?? [], ({ created_at: createdAt }) =>
      startOfDay(getDateFromServerTimestamp(createdAt))
    );

    return Object.values(result).map((messages) => ({
      title: formatDay(getDateFromServerTimestamp(messages[0].created_at)),
      data: messages,
    }));
  }, [chat?.messages]);

  const renderListItem = useCallback(
    ({
      id,
      text,
      read,
      model_id: modelId,
      created_at: createdAt,
    }: ChatMessageType) => {
      return (
        <ChatMessage
          key={id}
          self={!!modelId}
          date={getDateFromServerTimestamp(createdAt)}
          {...{
            text,
            read,
          }}
        />
      );
    },
    []
  );

  const header = useMemo(() => {
    if (isLoading) {
      return <Spinner em />;
    }
    // TODO: Receive "typing" pusher event

    // if (typing) {
    //   return <TextLabel style={$.typing}>Typing...</TextLabel>;
    // }

    return null;
  }, [isLoading]);

  const footer = useMemo(() => {
    // Last message
    const message = Array.from(chat?.messages ?? []).pop();

    if (!message) {
      return null;
    }

    const isFirstDay =
      !!chat?.created_at &&
      isSameDay(
        new Date(chat.created_at * 1000),
        new Date(message.created_at * 1000)
      );

    if (!isFirstDay) {
      return null;
    }

    return <Banner message="Messages are from Premium customers only" />;
  }, [chat?.created_at, chat?.messages]);

  return (
    <SectionList
      sections={messagesByDay}
      inverted
      renderItem={({ item }) => renderListItem(item)}
      renderSectionFooter={({ section }) => (
        <Row.C style={$.day}>
          <TextLabel>{section.title}</TextLabel>
        </Row.C>
      )}
      contentContainerStyle={[$.content, disabled && $.disabled]}
      ListHeaderComponent={header}
      ListFooterComponent={footer}
      onEndReached={onRefresh}
    />
  );
}

const COMPOSER_HEIGHT = 50;

const $ = createStyles({
  content: {
    col: 8,
    p: "2",
    alignItems: "stretch",
    paddingTop: COMPOSER_HEIGHT, // padding__Top__ because `inverted=true`
    backgroundColor: "surface",
  },
  day: {
    pt: "4",
    pb: "1",
    borderBottomColor: colors.outline,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  typing: {
    p: "2",
    fontStyle: "italic",
    color: "onSurfaceDim",
  },
  disabled: {
    opacity: 0.5,
  },
});
